import type { ReactNode } from 'react';
import { FormProvider, type FieldValues, type UseFormReturn } from 'react-hook-form';
import { SectionHeader } from './header';
import { Dialog, DialogContent, DialogFooter } from '@/shared/components/ui/dialog';
import { cn } from '@/shared/lib/cn';

export interface ModalFormProps<T extends FieldValues> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form?: UseFormReturn<T>;
  onSubmit?: (values: T) => void;
  title?: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  headerIcon?: ReactNode;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  showHeader?: boolean;
  showFooter?: boolean;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  'data-testid'?: string;
}

export function ModalForm<T extends FieldValues>({
  open,
  onOpenChange,
  form,
  onSubmit,
  title,
  description,
  children,
  footer,
  className,
  headerIcon,
  headerClassName,
  bodyClassName,
  footerClassName,
  showHeader = true,
  showFooter = true,
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  'data-testid': dataTestId,
}: ModalFormProps<T>) {
  const content = (
    <>
      {showHeader && (title || description) && (
        <SectionHeader
          sectionIcon={headerIcon ?? undefined}
          title={title}
          subtitle={description}
          className={headerClassName}
        />
      )}

      {form ? (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit!)} className="flex-1 flex flex-col gap-4">
            <div className={cn('flex flex-col gap-4 mt-4', bodyClassName)}>{children}</div>

            {showFooter && footer && (
              <DialogFooter className={footerClassName}>{footer}</DialogFooter>
            )}
          </form>
        </FormProvider>
      ) : (
        <div className={cn('flex flex-col gap-4 mt-4', bodyClassName)}>{children}</div>
      )}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent
        className={cn('sm:max-w-lg', className)}
        data-testid={dataTestId}
        showCloseButton={showCloseButton}
        onPointerDownOutside={(event) => {
          if (!closeOnOverlayClick) {
            event.preventDefault();
          }
        }}
        onEscapeKeyDown={(event) => {
          if (!closeOnEscape) {
            event.preventDefault();
          }
        }}
      >
        {content}
      </DialogContent>
    </Dialog>
  );
}

export { DialogFooter };
